<script setup lang="ts">
// Card expansível dos históricos do Minha Conta V2 (ENG-362).
// Cabeçalho compacto via slot default (+ slot #aside pro bloco da direita,
// ex.: valor) e detalhes expandidos via prop `rows` (pares label/valor)
// ou slot #details pra layouts customizados (ex.: coordenadas do login).
// Slot #details substitui as linhas; #footer entra DEPOIS delas, pra ações do
// card (ex.: "Ver comprovante" no histórico de saque, ENG-848).
interface HistoryCardRow {
  label: string;
  value: string;
}

interface Props {
  rows?: HistoryCardRow[];
  // badge de status opcional exibida no bloco da direita
  status?: string;
  statusVariant?: 'success' | 'warning' | 'danger';
  defaultExpanded?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  status: '',
  statusVariant: 'success',
  defaultExpanded: false,
});

const expanded = ref(props.defaultExpanded);

const badgeClasses: Record<NonNullable<Props['statusVariant']>, string> = {
  success: 'bg-v2-success/20 text-v2-success',
  warning: 'bg-v2-warning/20 text-v2-warning',
  danger: 'bg-v2-danger/20 text-v2-danger',
};
</script>

<template>
  <div class="w-full rounded-[10px] bg-v2-default/40 p-[12px]">
    <!-- div clicável (não <button>) pra permitir botões aninhados no slot, ex.: copiar ID -->
    <div
      class="flex w-full cursor-pointer items-center gap-[8px] text-left"
      role="button"
      tabindex="0"
      @click="expanded = !expanded"
      @keydown.enter="expanded = !expanded"
      @keydown.space.prevent="expanded = !expanded"
    >
      <slot />

      <div
        v-if="$slots.aside || props.status"
        class="ml-auto flex shrink-0 flex-col items-end gap-[4px]"
      >
        <slot name="aside" />
        <span
          v-if="props.status"
          class="rounded-[6px] px-[8px] py-[2px] text-[10px] font-medium leading-[14px]"
          :class="badgeClasses[props.statusVariant]"
        >
          {{ props.status }}
        </span>
      </div>

      <span
        class="flex shrink-0 items-center justify-center text-white"
        :class="{ 'rotate-180': expanded }"
      >
        <UserV2Icon name="chevron-down" />
      </span>
    </div>

    <div
      v-if="expanded"
      class="border-solid history-card-details mt-[8px] flex flex-col gap-[8px] border-t border-white/10 pt-[8px]"
    >
      <slot name="details">
        <div
          v-for="row in props.rows"
          :key="row.label"
          class="flex items-start justify-between gap-[16px] text-[12px] leading-[16px]"
        >
          <span class="shrink-0 text-v2-default-800">{{ row.label }}</span>
          <span class="min-w-0 truncate text-right text-white">
            {{ row.value }}
          </span>
        </div>
      </slot>

      <slot name="footer" />
    </div>
  </div>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` globalmente — animação de expandir via keyframes
.history-card-details {
  animation: history-card-expand 0.18s ease-out;
}

@keyframes history-card-expand {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
